import { useLayoutEffect, useRef, useState } from "react";

/**
 * Höhe, mit der ein Element den Rest des Fensters unter sich füllt — gemessen,
 * nicht geraten.
 *
 * Die Customer View gab ihren Scrollbereichen bisher `calc(100vh - 320px)`:
 * ein Schätzwert für Kopfzeile, Kundenwahl und Filterleiste. Sobald die
 * Filterleiste in die zweite Zeile umbrach (Presets, 2026-09-24), lag das
 * Ende des Detailbereichs 38 px unter der Fensterkante — genau dort, wo die
 * klebende Fußzeile mit „An den Kunden / Intern" und „Kommentar senden"
 * steht. Die Zeile war da, nur nicht zu sehen; das Textfeld darüber schon.
 *
 * Deshalb misst dieser Hook, wo das Element im Dokument beginnt, und gibt
 * ihm den Rest bis zum unteren Rand abzüglich `bottomGapPx` (der Innenabstand
 * der AppShell). Neu gemessen wird bei jeder Fenster- und Layoutänderung
 * (Banner erscheint, Leiste bricht um). Innere Bereiche füllen dann mit
 * `flex: 1; min-height: 0` — ohne eigene Formel.
 */
export function useFillViewport<T extends HTMLElement>(bottomGapPx = 16, minPx = 240) {
  const ref = useRef<T>(null);
  const [height, setHeight] = useState<number | undefined>(undefined);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const top = el.getBoundingClientRect().top + window.scrollY;
      setHeight(Math.max(minPx, Math.round(window.innerHeight - top - bottomGapPx)));
    };
    measure();
    window.addEventListener("resize", measure);
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(document.body);
    return () => {
      window.removeEventListener("resize", measure);
      observer?.disconnect();
    };
  }, [bottomGapPx, minPx]);

  return { ref, height };
}
